import { z } from 'zod';
import { Optional } from '@/generic/functional';
import { Authorities } from '@/supporting/account/domain/Authorities.ts';
import { type Authority, authorityOf } from '@/supporting/account/domain/Authority.ts';
import { Organization } from '@/supporting/account/domain/Organization.ts';
import { User } from '@/supporting/account/domain/User.ts';

const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  authorities: z.array(z.string()),
});

export type CleanApiOrganizationJson = z.infer<typeof organizationSchema>;

const userSchema = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string(),
  organization: organizationSchema.nullish(),
});

export type CleanApiUserJson = z.infer<typeof userSchema>;

const jsonToAuthorities = (authorities: string[]): Authorities =>
  Authorities.of(
    ...authorities.flatMap((authority) =>
      authorityOf(authority)
        .map((known): Authority[] => [known])
        .orElse([]),
    ),
  );

const jsonToOrganization = (json: CleanApiOrganizationJson): Organization =>
  Organization.of({
    id: json.id,
    name: json.name,
    authorities: jsonToAuthorities(json.authorities),
  });

const jsonToUser = (json: CleanApiUserJson): User =>
  User.of({
    firstName: Optional.ofFalsifiable(json.firstName).orUndefined(),
    lastName: Optional.ofFalsifiable(json.lastName).orUndefined(),
    email: json.email,
    organization: Optional.ofFalsifiable(json.organization).map(jsonToOrganization).orUndefined(),
  });

export const parseJsonToUser = (json: unknown): User => jsonToUser(userSchema.parse(json));
